import React, { useState, useEffect } from 'react';
import { Typography, Button, Tooltip, Icon, Spinner, Checkbox } from '@scale/scaleui';
import { SignupFlowType, IExtendedSignupFlow } from '@scale/shared-types';
import { SignupFlowConditionalQualificationModal } from './SignupFlowConditionalQualificationModal';
import Select from 'react-scale/corp/components/UI/Select';
import { useFetchScreeningConfigs, useFetchFastLaneChainQuery, getSignupFlowTags } from '../../api';
import { List, Flex } from '@scale/scaleui-radix';
import { SignupFlowIntendedWorkerSkills } from './SignupFlowIntendedWorkerSkills';
import SelectTag from 'react-scale/corp/components/Lookup/sections/SelectTag';
import { SignupFlowConditionSummary } from './SignupFlowConditionSummary';

const SignupFlowConditionalConfigInput: React.FC<{
  signupFlowConfig: Partial<IExtendedSignupFlow>;
  setSignupFlowConfig: (signupFlowConfig: Partial<IExtendedSignupFlow>) => void;
  signupFlowsForOnboardingFlow: {
    id: string;
    name: string;
    token: string;
    active?: boolean;
    type?: SignupFlowType;
  }[];
  disableFlowNavigation: boolean;
  setSignupFlowId: (signupFlowId: string) => void;
}> = ({
  signupFlowConfig,
  setSignupFlowConfig,
  signupFlowsForOnboardingFlow,
  setSignupFlowId,
  disableFlowNavigation,
}) => {
  const [isQualificationModalOpen, setIsQualificationModalOpen] = useState(false);
  const [fastLaneTags, setFastLaneTags] = useState<{ value: string; label: string }[]>([]);

  const { data: screeningConfigs, isLoading: isLoadingScreeningConfigs } =
    useFetchScreeningConfigs();

  const { data: fastLaneChainData } = useFetchFastLaneChainQuery(signupFlowConfig._id);
  const directParents = fastLaneChainData?.directParents || [];

  useEffect(() => {
    const fetchTags = async () => {
      const tags = await getSignupFlowTags(signupFlowConfig.conditionalTags ?? []);
      setFastLaneTags(tags.map(tag => ({ value: tag._id, label: tag.name })));
    };
    fetchTags();
  }, [signupFlowConfig.conditionalTags]);

  const fallbackFlowOptions = signupFlowsForOnboardingFlow.filter(
    flow =>
      flow.id !== signupFlowConfig._id &&
      !directParents.some((parent: { _id: string }) => parent._id === flow.id),
  );

  const hasConditions = (signupFlowConfig.conditionalConfig?.conditions?.length ?? 0) > 0;

  return (
    <div className="w-full gap-y-4 flex flex-col">
      {(hasConditions || signupFlowConfig.fallbackFlowId) && (
        <div className="w-full grid grid-cols-4 gap-x-3 items-start">
          <>
            <div className="col-span-1 flex items-center self-start pr-1">
              <Typography.Paragraph3>Flow falls back to*</Typography.Paragraph3>
              <Tooltip
                text="The signup flow to serve users who do not meet the qualification conditions."
                target={
                  <div>
                    <Icon icon={['fas', 'circle-info']} />
                  </div>
                }
                side="bottom"
              />
            </div>
            <div className="col-span-3 flex gap-x-2 items-center">
              <Select
                className="flex-1"
                options={fallbackFlowOptions.map(flow => ({
                  label: flow.name,
                  value: flow.id,
                }))}
                value={signupFlowConfig.fallbackFlowId || ''}
                onChange={(option: { label: string; value: string }) => {
                  setSignupFlowConfig({
                    ...signupFlowConfig,
                    fallbackFlowId: option?.value,
                  });
                }}
              />
              {signupFlowConfig.fallbackFlowId && (
                <Tooltip
                  text={
                    disableFlowNavigation
                      ? 'Please save changes before viewing a fallback flow.'
                      : ''
                  }
                  target={
                    <div>
                      <Button
                        onClick={() => {
                          if (signupFlowConfig.fallbackFlowId) {
                            setSignupFlowId(signupFlowConfig.fallbackFlowId);
                          }
                        }}
                        variant="secondary"
                        size="sm"
                        disabled={disableFlowNavigation}
                      >
                        View
                      </Button>
                    </div>
                  }
                  side="bottom"
                />
              )}
            </div>
          </>
        </div>
      )}

      {directParents.length > 0 && (
        <div className="w-full grid grid-cols-4 gap-x-3 items-start">
          <div className="col-span-1 flex items-center self-start pr-1">
            <Typography.Paragraph3>Flow is a fallback of</Typography.Paragraph3>
            <Tooltip
              text="Signup flows that will fall back to this flow if they do not meet the qualification conditions."
              target={
                <div>
                  <Icon icon={['fas', 'circle-info']} />
                </div>
              }
              side="bottom"
            />
          </div>
          <div className="col-span-3 flex flex-col gap-y-2">
            <List.Root>
              {directParents.map(parent => (
                <List.Item asChild key={parent._id}>
                  <Flex justify="between" align="center">
                    <Typography.Paragraph2 className="flex-1">{parent.name}</Typography.Paragraph2>
                    <Tooltip
                      text={
                        disableFlowNavigation ? 'Please save changes before viewing this flow.' : ''
                      }
                      target={
                        <div>
                          <Button
                            onClick={() => setSignupFlowId(parent._id)}
                            variant="secondary"
                            size="sm"
                            disabled={disableFlowNavigation}
                          >
                            View
                          </Button>
                        </div>
                      }
                      side="bottom"
                    />
                  </Flex>
                </List.Item>
              ))}
            </List.Root>
          </div>
        </div>
      )}

      <div className="w-full flex flex-col">
        <div className="flex justify-between w-full">
          <Typography.Subtitle3>Conditions</Typography.Subtitle3>
        </div>
        <Typography.Caption>
          {hasConditions
            ? "Users who don't meet the following conditions will be demoted to the fallback flow"
            : "Add conditions to modify a user's onboarding experience based on their credentials"}
        </Typography.Caption>
      </div>

      <SignupFlowConditionSummary
        signupFlowConfig={signupFlowConfig}
        setSignupFlowConfig={setSignupFlowConfig}
        setIsQualificationModalOpen={setIsQualificationModalOpen}
      />

      {hasConditions && (
        <>
          <div className="w-full flex flex-col">
            <Typography.Subtitle3>Custom Screening</Typography.Subtitle3>
            <Typography.Caption className="mb-2">
              Users who meet the above conditions can be served a custom screening
            </Typography.Caption>
            <div className="">
              {isLoadingScreeningConfigs ? (
                <Spinner size="sm" />
              ) : (
                <Select
                  options={(screeningConfigs ?? []).map(config => ({
                    label: config.title,
                    value: config._id,
                  }))}
                  placeholder="Select a skill screening"
                  value={signupFlowConfig.conditionalScreeningConfigId}
                  onChange={(option: { label: string; value: string }) => {
                    setSignupFlowConfig({
                      ...signupFlowConfig,
                      conditionalScreeningConfigId: option?.value,
                    });
                  }}
                />
              )}
            </div>
          </div>

          <div className="w-full gap-x-3">
            <Typography.Subtitle3>Custom Tags</Typography.Subtitle3>
            <Typography.Caption className="mb-2">
              Users who pass the above conditions can be granted specific tags
            </Typography.Caption>
            <SelectTag
              showTagId={false}
              value={fastLaneTags}
              onChange={tags => {
                setSignupFlowConfig({
                  ...signupFlowConfig,
                  conditionalTags: tags.map((tag: { value: string }) => tag.value),
                });
              }}
              multi={true}
            />
          </div>
          <div className="w-full gap-y-2">
            <div className="flex flex-col">
              <Typography.Subtitle3>Worker Skill Grants</Typography.Subtitle3>
              <Typography.Caption className="mb-2">
                Users who pass the above conditions will be granted
                {signupFlowConfig.skipSkillGrantIntersectionCheck
                  ? ' all of these skills '
                  : " the subset of these skills that they've self-selected "}
                upon
                {signupFlowConfig.conditionalScreeningConfigId
                  ? ' passing the conditional screening'
                  : ' finishing any skill screening'}
              </Typography.Caption>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Checkbox
                checked={signupFlowConfig.skipSkillGrantIntersectionCheck}
                onChange={e => {
                  const isChecked = e.target.checked;
                  setSignupFlowConfig({
                    ...signupFlowConfig,
                    skipSkillGrantIntersectionCheck: isChecked,
                  });
                }}
              />
              <Typography.Caption>
                Grant all intended worker skills regardless of self-selected intersection
              </Typography.Caption>
              <Tooltip
                text="This can cause CBs to end up with skills they don't know! Use only when your domain's worker skills are ambiguous / unlikely to be parsed from the CBs resume or selected manually."
                target={
                  <div>
                    <Icon icon={['fas', 'circle-info']} size="sm" />
                  </div>
                }
                side="bottom"
              />
            </div>
            <SignupFlowIntendedWorkerSkills
              signupFlowConfig={signupFlowConfig}
              setSignupFlowConfig={setSignupFlowConfig}
              shouldSetAutoGranted={true}
            />
          </div>
        </>
      )}

      <SignupFlowConditionalQualificationModal
        isModalOpen={isQualificationModalOpen}
        setIsModalOpen={setIsQualificationModalOpen}
        signupFlowConfig={signupFlowConfig}
        setSignupFlowConfig={setSignupFlowConfig}
      />
    </div>
  );
};

export default SignupFlowConditionalConfigInput;
